import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_SUPPORT,
  R2_RETENTION_SUPPORT,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SPONSOR_ITEM_AUTO,
  SPONSOR_NOTE,
  SPONSOR_PURPOSE,
  SPONSOR_RETENTION_ROW,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  admobOverseasRow,
  admobRow,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  rights,
  table,
} from './common'

/**
 * SoundTether 개인정보 처리방침.
 *
 * 오디오는 P2P 라 서버를 거치지 않는다 — 그 사실이 이 문서의 핵심이고, 서버 경유로
 * 바뀌는 날(TURN 도입 등) 이 문서를 먼저 고쳐야 한다 (docs/privacy-policy.md).
 */

const EFFECTIVE_DATE = 'August 20, 2026'
const LAST_REVISED = 'October 13, 2026'
const SESSION_RETENTION = '12 months'
const CRASH_RETENTION = '90 days'

/** 홈·청취 화면 배너와 연결 시점 전면광고. 광고 제거 소유자는 제외한다. */
const ADMOB_PURPOSE = 'Serving home and listening-screen banners and one interstitial at connection, unless ad removal is owned'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Pairing devices — matching a PC and a phone so they can connect',
      'Providing and distinguishing streaming sessions — managing sessions in progress and showing paired devices under their own names',
      'Keeping the service reliable and measuring usage — finding and fixing app errors, and computing quality figures such as session success rate',
      'Serving advertising — showing the ads that fund a free service. Ad data is processed by Google (AdMob); the Operator does not receive it',
      'Optional usage analytics — understanding referral channels, downloads, connection success, listening duration and purchase actions with Google Analytics for Firebase and Google Analytics 4',
      SUPPORT_PURPOSE,
      SPONSOR_PURPOSE,
    ],
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: [
      'The service works without an account, and the items below are everything the Operator processes.',
    ],
    bullets: [
      '[Required] Installation identifier — a random UUID generated on your device when the app is installed. It does not directly identify you as a person',
      '[Required] Device name — the name used to tell paired devices apart',
      '[Collected automatically] Session records — the start and end times of pairing and streaming sessions',
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier and Firebase installation ID, screen and action names, app version and build, device model, operating system and device state, error messages, stack traces, and crash traces',
      '[Collected automatically] Device information and advertising identifier used in serving ads — processed by Google (AdMob), which supplies the ads, for ad delivery and fraud prevention. The Operator does not receive this information',
      '[Optional, after consent] Analytics — pseudonymous app-instance or browser identifiers, device and browser information, approximate IP-derived location, language, app version, referral origin and campaign labels, page and screen names, download and store clicks, connection outcomes, listening duration and purchase or restore outcomes. No account ID, pairing code, peer IP, device name, audio, QR content or free-text error is sent to Analytics',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
      SPONSOR_ITEM_AUTO,
    ],
    note: 'No account, name, phone number, or precise location is collected. Google Analytics may derive approximate location from the network address. An email address is collected only where you type one into the support page yourself so that a reply can be sent, and a photograph only where you attach one to a support message yourself. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected. Diagnostics never include audio or camera video, and a support message carries nothing beyond what you write and attach.',
  },
  {
    heading: 'Article 3. Handling of audio and the camera',
    paragraphs: [
      'Audio travels peer to peer, directly between the PC and the phone. It is not stored on and does not pass through the Operator’s servers, and the Operator cannot access its content.',
      'The camera permission in the phone app is used only to read the QR code that establishes a connection. Nothing that appears in the viewfinder is stored or transmitted.',
      'Attaching a photograph to a support message needs no permission. The picker belongs to the operating system, and the app receives only the photographs you choose there.',
    ],
  },
  {
    heading: 'Article 4. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      [
        'Installation identifier and device name',
        'Until you unpair the device or ask for deletion (destroyed on request)',
        'Your consent',
      ],
      ['Session records', `${SESSION_RETENTION} from creation`, 'Your consent'],
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      ['Optional analytics', 'Event-level data: 2 months. User-level data: 14 months, renewed with new activity. Website analytics cookies: 60 days. Aggregated reports follow Google Analytics retention rules', 'Your optional consent'],
      [...SUPPORT_RETENTION_ROW],
      [...SPONSOR_RETENTION_ROW],
    ]),
    note: `The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute. ${SUPPORT_NOTE} ${SPONSOR_NOTE}`,
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      [...CRASHLYTICS_ROW],
      ['Google LLC (Google Analytics / Firebase Analytics)', 'Usage and acquisition analysis', 'Pseudonymous identifiers, device information, approximate location, referrals and usage events listed in Article 2', 'Google Analytics property retention setting and applicable Google retention rules'],
      cloudflareRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_ROW],
      admobRow(ADMOB_PURPOSE),
    ]),
    note: 'In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If what is entrusted changes, this policy is updated and notice given in advance.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'The processors named in Article 6 are located outside Korea, and the entrustment transfers personal data abroad as follows.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [...CRASHLYTICS_OVERSEAS_ROW],
      ['Google LLC', 'The United States and other countries where Google operates', 'Optional analytics data listed in Article 2', 'HTTPS from the device after analytics consent', 'Usage and acquisition analysis', 'Google Analytics property retention setting and applicable Google retention rules'],
      cloudflareOverseasRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_OVERSEAS_ROW],
      admobOverseasRow(ADMOB_PURPOSE),
    ]),
    note: `${OVERSEAS_REFUSAL} Optional Analytics can be refused without losing pairing, playback, downloads or purchases. Change Usage analytics in app settings or on the website.`,
  },
  DISPOSAL,
  rights(
    [
      'In the app — unpairing a device erases that device’s installation identifier and device name',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'device names, session records, and the text of support messages',
  ),
  {
    heading:
      'Installation and operation of automatic collection devices, and how to refuse them (including advertising)',
    paragraphs: [
      'The service shows home and listening-screen banners and one interstitial at the moment a connection to the PC is made, except for users who own ad removal. Ads are served by Google LLC’s AdMob. The Operator requests non-personalized ads only, which means your behavioural data is not used for targeted advertising. Even for non-personalized ads, however, Google may process device information, an advertising identifier (ADID/IDFA), and coarse IP-based location for ad delivery, fraud prevention, and frequency capping. That information goes from your device directly to Google without passing through the Operator, and Google’s handling of it is governed by the Google Privacy Policy (https://policies.google.com/privacy).',
      'You can refuse this collection by resetting or deleting your advertising identifier in device settings — Android: Settings → Privacy → Ads; iOS: Settings → Privacy & Security → Apple Advertising. Refusing leaves every feature of the service available to you, and non-personalized ads may still appear.',
      'The service offers optional one-time ad removal and “buy the developer a coffee” sponsorship; payment is carried out by the Apple App Store or Google Play, and the Operator processes only the purchase record described in Articles 3, 5, 7, and 8.',
    ],
    note: 'Usage analytics is optional and disabled until you allow it. Change Usage analytics in app settings or on the website to allow or decline collection. The website loads the Google tag and stores analytics cookies only after permission; declining stops collection and removes its analytics cookies. In the app, declining disables collection and resets local analytics data. Analytics advertising storage, ad user data and ad personalization are disabled. No user ID is set, and audio is never sent to Google. Consent withdrawal does not retroactively erase previously processed reports.',
  },
  {
    ...CHILDREN,
    note: 'The service asks children for no additional personal data, and requests non-personalized ads only.',
  },
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'October 13, 2026 — added optional, consent-based Google Analytics to the app and website for acquisition and usage analysis; updated the description of banners and one-time ad removal',
    'August 26, 2026 — the photographs attached to support messages moved from the Operator’s own server disk to Cloudflare R2 object storage. Articles 6 and 7 were amended accordingly: image files are now held by a processor outside Korea. What is collected, why, and how long it is kept did not change',
    'August 26, 2026 — the support screen moved out of the app to a page at cottonray.com/support which the app opens inside itself. What is collected, why, and how long it is kept did not change',
    'August 24, 2026 — updated the purposes, items collected, and retention periods following the addition of photo attachments (up to three, chosen by you) and an optional email address to the in-app support screen, which the Operator uses to send a single reply from no-reply@cottonray.com',
  'August 24, 2026 — updated the purposes, items collected, retention periods, entrustment, and notice of transfers abroad following the introduction of the optional “buy the developer a coffee” sponsorship (in-app purchase; receipts verified by RevenueCat)',
    'August 15, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad)',
    'August 11, 2026 — updated the purposes, items collected, entrustment, transfers abroad, and automatic collection clauses following the introduction of an interstitial ad at connection time (Google AdMob, non-personalized)',
    'August 20, 2026 — updated the purposes, items collected, retention periods, and how to exercise your rights following the introduction of in-app support messages (feedback only, no contact details taken)',
    'August 19, 2026 — the site language changed to English and this policy was reissued in English. Nothing about how data is handled changed',
  ]),
]

export const SOUNDTETHER_POLICY: PolicyDocument = {
  slug: 'soundtether',
  title: 'SoundTether Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for SoundTether (“the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
